import React, { ChangeEvent, Dispatch, SetStateAction, useEffect, useState } from 'react'
import { SettingItem, SettingsCard } from '../../components/settings/SettingsCard'
import { axiosGet, axiosPatch } from '../../util/api'
import { Toasts } from '../../components/toaster/Toast'
import { ImpactMultiplierI } from '../../types/impact_multiplier'
import { KDPExclusiveBadge } from '../../components/badge/KDPExclusiveBadge'
import { Input } from '../../components/inputs/Input'
import { isValidNumericInput } from '../../util/numeric_input'
import type { EntityId } from '../../types/constants'

interface ImpactMultiplierProps {
    selectedProfile: Models.Profile
}

const profileHeader = (profileId: EntityId) => ({
    headers: {
        ProfileId: profileId,
    },
})

export const ImpactMultiplier = ({ selectedProfile }: ImpactMultiplierProps) => {
    const [loading, setLoading] = useState(false)
    const [profitMargin, setProfitMargin] = useState<number | string>(1)
    const [ordersMultiplier, setOrdersMultiplier] = useState<number | string>(1)
    const [kenpMultiplier, setKenpMultiplier] = useState<number | string>(1)
    const [dirty, setDirty] = useState(false)

    const set =
        (set: Dispatch<SetStateAction<number | string>>) => (e: ChangeEvent<HTMLInputElement>) => {
            const v = e.target.value
            if (isValidNumericInput(v)) {
                set(v)
                setDirty(true)
            }
        }

    useEffect(() => {
        setLoading(true)
        axiosGet('impact-multipliers', null, false, profileHeader(selectedProfile.profile_id))
            .then((res) => {
                const im = res.data as ImpactMultiplierI
                setProfitMargin(im.profit_margin / 100.0)
                setOrdersMultiplier(im.orders_impact_multiplier / 100.0)
                setKenpMultiplier(im.kenp_read_impact_multiplier / 100.0)
                setLoading(false)
            })
            .catch(() => {
                setLoading(false)
            })
    }, [selectedProfile.profile_id])

    const handleSave = () => {
        setLoading(true)
        axiosPatch(
            `impact-multipliers`,
            {
                profit_margin: parseFloat('' + profitMargin) * 100,
                orders_impact_multiplier: parseFloat('' + ordersMultiplier) * 100,
                kenp_read_impact_multiplier: parseFloat('' + kenpMultiplier) * 100,
            },
            false,
            profileHeader(selectedProfile.profile_id),
        )
            .then((res) => {
                const im = res.data as ImpactMultiplierI
                setProfitMargin(im.profit_margin / 100.0)
                setOrdersMultiplier(im.orders_impact_multiplier / 100.0)
                setKenpMultiplier(im.kenp_read_impact_multiplier / 100.0)
                setLoading(false)
                setDirty(false)
                Toasts.success('Impact multipliers saved')
            })
            .catch(() => {
                setLoading(false)
            })
    }

    if (!selectedProfile.is_kdp) {
        return null
    }

    return (
        <SettingsCard open title={<Title />} onSave={dirty ? handleSave : null} loading={loading}>
            <SettingItem
                title="Profit Margin"
                description="Multiplies your adjusted sales in order to determine your
            estimated profit from your book sales. This gives a more accurate way
            to combine your book sales and KENP royalties to make better advertising
            decisions."
                rightComponent={
                    <Input
                        style={{ width: '100px' }}
                        value={profitMargin}
                        onChange={set(setProfitMargin)}
                        suffix="%"
                    />
                }
            />
            <SettingItem
                title="Orders Impact Multiplier"
                description="Increases your reported order count and 'blended' sales revenue,
            enhancing metrics like ‘Blended Sales’ and ‘Blended ACOS’ for a truer
            depiction of your ads' overall sales impact. This multiplier captures
            both the lift in organic orders due to increased product visibility and
            ranking, and additional purchases across your catalog. This multiplier
            enables sellers to gauge the true effectiveness of their advertising and
            its influence on organic sales growth."
                rightComponent={
                    <Input
                        style={{ width: '100px' }}
                        value={ordersMultiplier}
                        onChange={set(setOrdersMultiplier)}
                    />
                }
            />
            <SettingItem
                title="KENP Read Impact Multiplier"
                description="Increases the number of KENP pages read and ‘adjusted’
            royalties, enhancing metrics such as ‘Adjusted Estimated Royalties’
            and ‘Adjusted ROAS’ to more accurately reflect your advertising's
            long-term effect on readership. By capturing pages read beyond the
            14-day attribution window and subsequent engagement of other books
            in your catalog, this multiplier offers a truer measure of the
            ongoing royalties generated by your advertising efforts."
                rightComponent={
                    <Input
                        style={{ width: '100px' }}
                        value={kenpMultiplier}
                        onChange={set(setKenpMultiplier)}
                    />
                }
                bottomDivider={false}
            />
        </SettingsCard>
    )
}

const Title = () => {
    return (
        <div className="flex items-center">
            Impact Multipliers
            <KDPExclusiveBadge />
        </div>
    )
}
